import React, { useState, useEffect, useRef } from "react";
import { Modal } from "Components/Modals/Modal";
import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { useSelector } from "react-redux";
import { CartItem } from "Dtos/IBudget";
import { Documents as DocumentView } from "Pages/ServiceForm/SelectProducts/Document";
import { createToast } from "Utils/toastFunc";
import { confirmBudgetAIError } from "Requests/Budget";
import { Documents } from "../Document";
import * as S from "./styles";

interface ConvertOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  hasDrive?: boolean;
  budgetId: number;
  fileRead?: {
    id: string;
    file_path: string;
    filename: string;
    preview: string;
    isImage: boolean;
  }[];
  files: { file_path: string }[];
  products: CartItem[];
}

export const ConvertOrderModal: React.FC<ConvertOrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  files,
  products,
  hasDrive = true,
  fileRead,
  budgetId,
}) => {
  const DocumentsToRender = Documents;
  const userData = useSelector((state: any) => state.User?.userData);
  const userName = userData?.name || "";

  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(
    new Set()
  );
  const [enabledCheckboxes, setEnabledCheckboxes] = useState<Set<number>>(
    new Set()
  );

  const [errorBudget, setErrorBudget] = useState(false);
  const [loadingCheckboxError, setLoadingCheckboxError] = useState(false);
  const timersRef = useRef<{ [key: number]: NodeJS.Timeout }>({});
  const enabledCheckboxesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    enabledCheckboxesRef.current = enabledCheckboxes;
  }, [enabledCheckboxes]);

  useEffect(() => {
    if (isOpen && products.length > 0) {
      const initialEnabled = new Set([0]);
      setEnabledCheckboxes(initialEnabled);
      enabledCheckboxesRef.current = initialEnabled;
      setSelectedProducts(new Set([0]));

      Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
      timersRef.current = {};
    }

    return () => {
      Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
      timersRef.current = {};
    };
  }, [isOpen, products]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedProducts(new Set());
      setEnabledCheckboxes(new Set());
      Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
      timersRef.current = {};
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedProducts.size > 0 && products.length > 0) {
      const selectedIndices = Array.from(selectedProducts);
      if (selectedIndices.length === 0) return;

      const lastSelectedIndex = Math.max(...selectedIndices);
      const nextIndex = lastSelectedIndex + 1;

      if (
        nextIndex < products.length &&
        !enabledCheckboxesRef.current.has(nextIndex)
      ) {
        if (timersRef.current[nextIndex]) {
          clearTimeout(timersRef.current[nextIndex]);
        }

        const timer = setTimeout(() => {
          setEnabledCheckboxes((prev) => {
            if (prev.has(nextIndex)) {
              return prev;
            }
            const newSet = new Set(prev);
            newSet.add(nextIndex);
            enabledCheckboxesRef.current = newSet;
            return newSet;
          });
          delete timersRef.current[nextIndex];
        }, 1000);

        timersRef.current[nextIndex] = timer;
      }
    }
  }, [selectedProducts, products.length]);

  const handleCheckboxChange = (index: number) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSetErrorBudget = () => setErrorBudget(!errorBudget);

  const allSelected =
    products.length > 0 && selectedProducts.size === products.length;
  const canConfirm = allSelected;

  const handleConfirmModal = async () => {
    try {
      setLoadingCheckboxError(true);

      if (errorBudget) {
        await confirmBudgetAIError(Number(budgetId));
      }

      onConfirm();
    } catch (error) {
      createToast("error", "Erro ao confirmar conversão em pedido");
    } finally {
      setLoadingCheckboxError(false);
    }
  };

  return (
    <Modal
      closeOverlay={false}
      isOpen={isOpen}
      maxWidth="1280"
      maxHeight="900"
      requestClose={onClose}
      renderHeading={() => (
        <h2 className="text-center">Confirmar conversão em pedido</h2>
      )}
      renderDescription={() => (
        <S.ModalContent>
          <S.ContentWrapper>
            <S.LeftSide>
              <h3>Pedido Médico</h3>

              {hasDrive && <DocumentsToRender files={files} />}

              {!hasDrive && fileRead ? <DocumentView files={fileRead} /> : null}
            </S.LeftSide>

            <S.RightSide>
              <h3>Produtos</h3>
              <S.ProductsList>
                {products.map((product, index) => {
                  const isEnabled = enabledCheckboxes.has(index);
                  const isSelected = selectedProducts.has(index);

                  return (
                    <S.ProductItem key={product.product_id || index}>
                      <S.CheckboxWrapper>
                        <input
                          type="checkbox"
                          id={`product-${index}`}
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(index)}
                          disabled={!isEnabled}
                          style={{
                            cursor: isEnabled ? "pointer" : "not-allowed",
                            opacity: isEnabled ? 1 : 0.5,
                          }}
                        />
                        <label
                          htmlFor={`product-${index}`}
                          style={{
                            cursor: isEnabled ? "pointer" : "not-allowed",
                            opacity: isEnabled ? 1 : 0.5,
                          }}
                        >
                          {product.product_name || `Produto ${index + 1}`}
                          {product.amount > 1 && ` (x${product.amount})`}
                        </label>
                      </S.CheckboxWrapper>
                    </S.ProductItem>
                  );
                })}
              </S.ProductsList>
            </S.RightSide>
          </S.ContentWrapper>

          {/* Rodapé com nome e botão */}
          <S.Footer>
            <S.ConfirmationText>
              Eu, <strong>{userName}</strong>, confirmo que todos os produtos
              acima foram selecionados corretamente.
            </S.ConfirmationText>

            <div className="error-budget">
              <S.CheckboxWrapper>
                <input
                  type="checkbox"
                  id="error-budget"
                  checked={errorBudget}
                  onChange={handleSetErrorBudget}
                  style={{
                    cursor: "pointer",
                    opacity: 1,
                  }}
                />
                <label
                  htmlFor="error-budget"
                  style={{
                    cursor: "pointer",
                    opacity: 1,
                  }}
                >
                  Confirmo que este orçamento apresenta erros.
                </label>
              </S.CheckboxWrapper>
            </div>
          </S.Footer>
        </S.ModalContent>
      )}
      renderActions={() => (
        <Box display="flex" justify="center" gap="10" pb="30">
          <Button outlined onClick={onClose}>
            Voltar
          </Button>
          <Button
            onClick={handleConfirmModal}
            disabled={!canConfirm}
            loading={loadingCheckboxError}
          >
            Confirmar
          </Button>
        </Box>
      )}
    />
  );
};
